<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-icon left>mdi-clock-outline</v-icon>
      <h5 class="font-weight-bold text-h5 text-typo mb-0">打卡紀錄</h5>
      <v-spacer />
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        size="24"
      />
    </v-card-title>

    <v-card-text>
      <v-row>
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                @click="changeDate(-1)"
                class="mr-1"
              >
                <v-icon size="24">mdi-chevron-left</v-icon>
              </v-btn>
            </template>
            <span>昨天</span>
          </v-tooltip>

          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="selectedDate"
                label="選擇日期"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDate"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="selectedDate"
              @input="dateMenu = false"
              locale="zh-TW"
              color="blue"
            ></v-date-picker>
          </v-menu>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
                @click="changeDate(1)"
                class="ml-1"
              >
                <v-icon size="24">mdi-chevron-right</v-icon>
              </v-btn>
            </template>
            <span>明天</span>
          </v-tooltip>
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="searchQuery"
            label="搜尋員工姓名"
            prepend-icon="mdi-magnify"
            clearable
            @input="handleSearchInput"
          ></v-text-field>
        </v-col>
        <!-- 匯入按鈕 -->
        <v-col cols="12" md="4" class="d-flex justify-end">
          <v-btn
            :loading="uploading"
            :disabled="uploading"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            @click="triggerClockRecordsUpload"
          >
            <v-icon size="18">mdi-plus</v-icon>
            匯入</v-btn
          >
        </v-col>
      </v-row>
    </v-card-text>

    <v-dialog v-model="clockRecordsUploadDialog" max-width="600px">
      <v-card class="card-shadow border-radius-xl">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0"
            >匯入打卡紀錄</span
          >
        </div>
        <v-card-text class="card-padding">
          <v-container class="px-0">
            <v-row>
              <v-col cols="12">
                <v-file-input
                  v-model="clockRecordsUploadFile"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  label="選擇 Excel 檔案 (.xlsx)"
                  prepend-icon="mdi-file-excel"
                  dense
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" class="d-flex align-center">
                <v-icon size="18" class="me-2"
                  >mdi-file-download-outline</v-icon
                >
                <a
                  :href="sampleTemplateUrl"
                  target="_blank"
                  rel="noopener"
                  download
                >
                  下載範例檔
                </a>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn
            @click="cancelClockRecordsUpload"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
              me-2
            "
            :disabled="uploading"
            >取消</v-btn
          >
          <v-btn
            @click="confirmClockRecordsUpload"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
            :loading="uploading"
            :disabled="uploading || !clockRecordsUploadFile"
            >確認</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table
      :headers="headers"
      :items="filteredRecords"
      :loading="loading"
      :server-items-length="totalItems"
      :items-per-page="itemsPerPage"
      class="table"
      hide-default-footer
      disable-pagination
      @page-count="pageCount = $event"
      @update:items-per-page="handleItemsPerPageChange"
    >
      <template v-slot:progress>
        <v-progress-linear indeterminate color="primary" />
      </template>

      <!-- Timeline Status Slot (demo.html: 狀態) -->
      <template v-slot:item.timeline_status="{ item }">
        <div class="d-flex align-center flex-wrap py-1" style="gap: 8px">
          <template v-for="event in getTimelineEvents(item)">
            <!-- GPS Menu for events with coordinates -->
            <v-menu
              v-if="isValidGPS(event.gps)"
              :key="event.id"
              :value="activeGPSKey === event.gpsKey"
              @input="(val) => (activeGPSKey = val ? event.gpsKey : null)"
              offset-y
              :close-on-content-click="false"
              min-width="250"
            >
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  :class="[
                    'text-sm font-weight-medium',
                    event.isAlert ? 'red--text' : 'text-body font-weight-bold',
                  ]"
                  style="cursor: pointer; border-bottom: 1px dashed #cb0c9f"
                  :title="'點擊查看 GPS: ' + event.gps"
                >
                  {{ event.label }}
                </span>
              </template>
              <v-card class="card-shadow border-radius-xl">
                <v-card-title
                  class="pa-2 d-flex justify-space-between align-center"
                >
                  <span class="text-subtitle-2 ml-2">{{
                    event.gpsTitle || "GPS 位置"
                  }}</span>
                  <v-btn icon x-small @click="activeGPSKey = null">
                    <v-icon size="16">mdi-close</v-icon>
                  </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pa-3">
                  <div class="mb-3 text-body-2 font-weight-bold">
                    {{ event.gps }}
                  </div>
                  <v-btn
                    block
                    small
                    color="primary"
                    class="bg-gradient-primary"
                    @click="openGPSInGoogleMaps(event.gps)"
                  >
                    <v-icon left size="16">mdi-map-marker</v-icon>
                    開啟地圖
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-menu>

            <!-- Standard text item without GPS -->
            <span
              v-else
              :key="event.id"
              :class="[
                'text-sm',
                event.isAlert ? 'red--text font-weight-bold' : 'text-body',
              ]"
            >
              {{ event.label }}
            </span>
          </template>
        </div>
      </template>

      <!-- Notes Slot (demo.html: 備註) -->
      <template v-slot:item.notes="{ item }">
        <div class="d-flex flex-wrap align-center py-1" style="gap: 6px">
          <span
            v-for="(n, idx) in getNotesInfo(item)"
            :key="idx"
            :class="['text-sm', n.isAlert ? 'red--text font-weight-bold' : '']"
            :style="n.color ? { color: n.color } : {}"
          >
            {{ n.text }}
          </span>
        </div>
      </template>

      <!-- Header Slots for compact numeric columns (ignore header text width) -->
      <template v-slot:header.regular_hours>
        <span class="d-inline-block text-center header-multiline"
          >正常<br />工時</span
        >
      </template>
      <template v-slot:header.overtime_tier1>
        <span class="d-inline-block text-center header-multiline"
          >前2小時<br />加班</span
        >
      </template>
      <template v-slot:header.overtime_tier2>
        <span class="d-inline-block text-center header-multiline"
          >第3小時<br />後加班</span
        >
      </template>

      <!-- Regular Work Hours Slot (demo.html: 正常工時) -->
      <template v-slot:item.regular_hours="{ item }">
        <span class="text-sm text-body font-weight-bold">
          {{
            item.regular_hours !== undefined &&
            item.regular_hours !== null &&
            item.regular_hours !== ""
              ? item.regular_hours
              : "-"
          }}
        </span>
      </template>

      <!-- Overtime Tier 1 Slot (demo.html: 前2小時加班) -->
      <template v-slot:item.overtime_tier1="{ item }">
        <span class="text-sm text-body">
          {{
            item.overtime_tier1 !== undefined &&
            item.overtime_tier1 !== null &&
            item.overtime_tier1 !== ""
              ? item.overtime_tier1
              : ""
          }}
        </span>
      </template>

      <!-- Overtime Tier 2 Slot (demo.html: 第3小時後加班) -->
      <template v-slot:item.overtime_tier2="{ item }">
        <span class="text-sm text-body">
          {{
            item.overtime_tier2 !== undefined &&
            item.overtime_tier2 !== null &&
            item.overtime_tier2 !== ""
              ? item.overtime_tier2
              : ""
          }}
        </span>
      </template>
      <template v-slot:item.user_name="{ item }">
        <div class="d-flex align-center">
          <span
            class="text-sm font-weight-bold text-body hover-scale"
            style="cursor: pointer; text-decoration: underline"
            @click="
              item.user && item.user.id
                ? $emit('view-monthly', item.user.id)
                : null
            "
            title="查看每月打卡紀錄"
          >
            {{ item.user && item.user.name ? item.user.name : "" }}
          </span>
          <span
            v-if="item.businessTripOutingRecord"
            class="red--text font-weight-bold ml-1 text-sm"
          >
            ({{
              item.businessTripOutingRecord.category === "business_trip"
                ? "出差"
                : "外出"
            }})
          </span>
        </div>
      </template>
      <!-- actions -->
      <template v-slot:item.actions="{ item }">
        <div v-if="rootRole" class="d-flex align-center">
          <v-btn
            @click.stop="editItem(item)"
            icon
            elevation="0"
            :ripple="false"
            height="28"
            min-width="36"
            width="36"
            class="btn-ls me-2 my-2 rounded-sm"
            color="#67748e"
            title="編輯"
          >
            <v-icon size="18">mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            @click.stop="viewHistory(item)"
            icon
            elevation="0"
            :ripple="false"
            height="28"
            min-width="36"
            width="36"
            class="btn-ls me-2 my-2 rounded-sm"
            color="#cb0c9f"
            title="歷史記錄"
          >
            <v-icon size="18">mdi-history</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:body.append>
        <tr v-if="error">
          <td
            :colspan="headers.length"
            class="text-center red--text text-body-2"
          >
            {{ error }}
          </td>
        </tr>
        <tr v-else-if="!loading && !records.length">
          <td
            :colspan="headers.length"
            class="text-center grey--text text-body-2"
          >
            無資料
          </td>
        </tr>
      </template>
    </v-data-table>

    <div id="sentinel-clock" class="d-flex justify-center py-4">
      <v-progress-circular
        v-if="loadingMore"
        indeterminate
        color="#cb0c9f"
        size="24"
      ></v-progress-circular>
    </div>

    <info-dialog
      :visible.sync="infoDialog.visible"
      :title="infoDialog.title"
      :message="infoDialog.message"
      :yes-button-text="infoDialog.yesButtonText"
      :yes-callback="infoDialog.yesCallback"
    />

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="600px">
      <v-card class="card-shadow border-radius-xl" v-if="editedItem">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0">
            編輯打卡紀錄 -
            {{ editedItem.user ? editedItem.user.name + " - " : ""
            }}{{ editedItem.clock_date || selectedDate }}
          </span>
        </div>
        <v-card-text class="card-padding">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="clockInActualTime"
                label="實際上班時間 (HH:mm)"
                placeholder="08:00"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="clockOutActualTime"
                label="實際下班時間 (HH:mm)"
                placeholder="17:00"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="lunchBreakStartActualTime"
                label="休息A開始時間 (HH:mm)"
                placeholder="12:00"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="lunchBreakEndActualTime"
                label="休息A結束時間 (HH:mm)"
                placeholder="13:00"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="editedItem.late"
                label="遲到 (分鐘)"
                type="number"
                dense
                outlined
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="editedItem.early"
                label="早退 (分鐘)"
                type="number"
                dense
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn text @click="cancelEdit" class="mr-2">取消</v-btn>
          <v-btn color="primary" class="bg-gradient-primary" @click="saveItem">
            儲存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="historyDialog" max-width="900px" scrollable>
      <v-card class="card-shadow border-radius-xl">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0"
            >修改歷史記錄</span
          >
        </div>
        <v-card-text class="card-padding" style="max-height: 600px">
          <v-progress-linear
            v-if="historyLoading"
            indeterminate
            color="primary"
          />
          <div v-else-if="historyError" class="red--text text-center py-4">
            {{ historyError }}
          </div>
          <div
            v-else-if="!historyRecords.length"
            class="grey--text text-center py-4"
          >
            無修改記錄
          </div>
          <v-timeline v-else dense>
            <v-timeline-item
              v-for="record in historyRecords"
              :key="record.id"
              small
              color="primary"
            >
              <template v-slot:opposite>
                <span class="text-caption">
                  {{ formatDateTime(record.create_time) }}
                </span>
              </template>
              <v-card class="elevation-2">
                <v-card-text>
                  <div class="font-weight-bold mb-2">
                    修改人: {{ record.modifier ? record.modifier.name : "-" }}
                    <span
                      v-if="record.modifier && record.modifier.email"
                      class="text-caption grey--text"
                    >
                      ({{ record.modifier.email }})
                    </span>
                  </div>
                  <div class="text-caption grey--text mb-2">
                    {{ formatDateTime(record.create_time) }}
                  </div>
                  <v-divider class="my-2" />
                  <div class="text-body-2">
                    <div
                      v-for="(change, field) in record.changes"
                      :key="field"
                      class="mb-2"
                    >
                      <strong>{{ getFieldLabel(field) }}:</strong>
                      <div class="ml-4">
                        <div class="red--text text--lighten-1">
                          舊值: {{ formatFieldValue(field, change.old) }}
                        </div>
                        <div class="green--text text--darken-1">
                          新值: {{ formatFieldValue(field, change.new) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
          <div v-if="historyMeta.totalPages > 1" class="text-center mt-4">
            <v-pagination
              v-model="historyPage"
              :length="historyMeta.totalPages"
              @input="loadHistory"
              circle
              color="primary"
            />
          </div>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn
            @click="closeHistory"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
            "
            >關閉</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import InfoDialog from "@/components/Common/InfoDialog.vue";
import { formatDateTime as formatDateTimeUtil } from "@/utils/time";
import { LEAVE_TYPE } from "@/constants";

const getTodayInTaipei = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

export default {
  name: "DailyClockRecordsV2",
  components: {
    InfoDialog,
  },
  props: {
    date: {
      type: String,
      default: () => {
        return getTodayInTaipei();
      },
    },
  },
  data() {
    return {
      records: [],
      loading: false,
      error: null,
      fetchError: false,
      selectedDate: getTodayInTaipei(),
      dateMenu: false,
      searchQuery: "",
      debouncedSearch: "",
      searchDebounceTimer: null,
      uploading: false,
      clockRecordsUploadDialog: false,
      clockRecordsUploadFile: null,
      infoDialog: {
        visible: false,
        title: "匯入結果",
        message: "",
        yesButtonText: "確定",
        yesCallback: null,
      },
      pageCount: 0,
      currentPage: 1,
      itemsPerPage: 15,
      totalItems: 0,
      editDialog: false,
      editedItem: null,
      originalItem: null,
      clockInActualDate: null,
      clockInActualTime: null,
      clockOutActualDate: null,
      clockOutActualTime: null,
      clockInTimeDate: null,
      clockInTimeTime: null,
      clockOutTimeDate: null,
      clockOutTimeTime: null,
      lunchBreakStartDate: null,
      lunchBreakStartTime: null,
      lunchBreakStartActualDate: null,
      lunchBreakStartActualTime: null,
      lunchBreakEndDate: null,
      lunchBreakEndTime: null,
      lunchBreakEndActualDate: null,
      lunchBreakEndActualTime: null,
      historyDialog: false,
      historyLoading: false,
      historyError: null,
      historyRecords: [],
      historyMeta: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      historyPage: 1,
      currentHistoryRecordId: null,
      headers: [
        {
          text: "姓名",
          value: "user_name",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "120px",
        },
        {
          text: "狀態",
          value: "timeline_status",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "60%",
        },
        {
          text: "備註",
          value: "notes",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "正常工時",
          value: "regular_hours",
          sortable: false,
          align: "center",
          class:
            "text-secondary font-weight-bolder opacity-7 black--text px-1 number-col-header",
          cellClass: "px-1 number-col-cell",
          width: "60px",
        },
        {
          text: "前2小時加班",
          value: "overtime_tier1",
          sortable: false,
          align: "center",
          class:
            "text-secondary font-weight-bolder opacity-7 black--text px-1 number-col-header",
          cellClass: "px-1 number-col-cell",
          width: "60px",
        },
        {
          text: "第3小時後加班",
          value: "overtime_tier2",
          sortable: false,
          align: "center",
          class:
            "text-secondary font-weight-bolder opacity-7 black--text px-1 number-col-header",
          cellClass: "px-1 number-col-cell",
          width: "60px",
        },
        {
          text: "操作",
          value: "actions",
          sortable: false,
          align: "center",
          class: "text-secondary font-weight-bolder opacity-7 black--text",
          width: "90px",
        },
      ],
      activeGPSKey: null,
      loadingMore: false,
      observer: null,
    };
  },
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    ...mapGetters("user", ["rootRole"]),
    sampleTemplateUrl() {
      return `${process.env.VUE_APP_API_URL}/resource/samples/clock_history.xlsx`;
    },
    filteredRecords() {
      const query = (this.debouncedSearch || "").trim().toLowerCase();
      if (!query) {
        return this.records;
      }
      return this.records.filter((record) => {
        const userName =
          record.user && record.user.name ? record.user.name.toLowerCase() : "";
        return userName.includes(query);
      });
    },
  },
  created() {
    this.debouncedSearch = this.searchQuery;
    this.fetchAttendanceRecords();
  },
  mounted() {
    this.initIntersectionObserver();
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  watch: {
    selectedDate() {
      this.currentPage = 1;
      this.fetchAttendanceRecords();
    },
    selectedCompany: {
      handler() {
        this.currentPage = 1;
        this.fetchAttendanceRecords();
      },
      deep: true,
    },
  },
  methods: {
    async fetchAttendanceRecords(append = false) {
      if (append) {
        this.loadingMore = true;
      } else {
        this.loading = true;
      }
      this.error = null;
      if (!this.selectedCompany) {
        this.records = [];
        this.loading = false;
        this.loadingMore = false;
        return;
      }
      this.fetchError = false;
      const queryParams = new URLSearchParams({
        page: this.currentPage,
        limit: this.itemsPerPage,
        start_date: this.selectedDate,
        end_date: this.selectedDate,
        company_id: this.selectedCompany.id,
      });

      try {
        const response = await this.$axios.get(
          `/api/clock-records/v2?${queryParams}`,
        );
        if (append) {
          this.records = [...this.records, ...(response.data.rows || [])];
        } else {
          this.records = response.data.rows || [];
        }

        this.totalItems = response.data.pagination
          ? response.data.pagination.totalItems
          : 0;
      } catch (error) {
        this.records = [];
        this.error =
          error?.response?.data?.message ||
          error.message ||
          "取得資料時發生錯誤";
        this.fetchError = true;
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    initIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      };
      this.observer = new IntersectionObserver((entries) => {
        const target = entries[0];
        if (
          target.isIntersecting &&
          !this.loading &&
          !this.loadingMore &&
          !this.fetchError &&
          this.currentPage < this.pageCount
        ) {
          this.currentPage += 1;
          this.fetchAttendanceRecords(true);
        }
      }, options);

      const target = document.querySelector("#sentinel-clock");
      if (target) {
        this.observer.observe(target);
      }
    },
    formatDateTime(value) {
      return formatDateTimeUtil(value);
    },
    getOvertimeRecords(item) {
      if (!item) return [];
      if (Array.isArray(item.overtimeRecords)) {
        return item.overtimeRecords;
      }
      if (item.overtimeRecord && typeof item.overtimeRecord === "object") {
        return [item.overtimeRecord];
      }
      return [];
    },
    getTotalOvertimeMinutes(item) {
      return this.getOvertimeRecords(item).reduce((sum, ot) => {
        const minutes = Number(ot && ot.overtime_minutes);
        return sum + (Number.isFinite(minutes) ? minutes : 0);
      }, 0);
    },
    getLeaveTypeName(typeId) {
      return LEAVE_TYPE[typeId] || `假別${typeId}`;
    },

    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
      this.fetchAttendanceRecords();
    },
    handleSearchInput() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearch = this.searchQuery;
        this.currentPage = 1;
      }, 300);
    },
    triggerClockRecordsUpload() {
      if (this.uploading) return;
      this.clockRecordsUploadFile = null;
      this.clockRecordsUploadDialog = true;
    },

    cancelClockRecordsUpload() {
      if (this.uploading) return;
      this.clockRecordsUploadDialog = false;
      this.clockRecordsUploadFile = null;
    },
    async confirmClockRecordsUpload() {
      if (!this.clockRecordsUploadFile) {
        alert("請先選擇要匯入的檔案");
        return;
      }
      this.clockRecordsUploadDialog = false;
      await this.uploadClockRecordsFile(this.clockRecordsUploadFile);
    },
    async uploadClockRecordsFile(file) {
      if (!file) return;
      if (!this.selectedCompany || !this.selectedCompany.id) {
        alert("請先選擇公司再匯入");
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("company_id", this.selectedCompany.id);
      this.uploading = true;
      try {
        const response = await this.$axios.post(
          "/api/clock-records/bulk-upload",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        const { message, summary } = response?.data || {};
        const successCount = summary?.success;
        const errorCount = summary?.errors;
        const errorDetails = (response?.data?.results?.errors || [])
          .map((err) => `第 ${err.row} 列 - ${err.error}`)
          .join("\n");
        const summaryMessage =
          message ||
          `匯入完成${
            typeof successCount === "number" ? `，成功 ${successCount} 筆` : ""
          }${typeof errorCount === "number" ? `，失敗 ${errorCount} 筆` : ""}`;
        this.infoDialog.message = summaryMessage;
        if (errorDetails && errorCount > 0) {
          this.infoDialog.message += `\n\n失敗明細:\n${errorDetails}`;
        }
        this.infoDialog.title = "匯入結果";
        this.infoDialog.yesCallback = async () => {
          await this.fetchAttendanceRecords();
          this.infoDialog.yesCallback = null;
        };
        this.infoDialog.visible = true;
      } catch (error) {
        const errMsg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error.message ||
          "匯入失敗";
        this.infoDialog.message = errMsg;
        this.infoDialog.title = "匯入失敗";
        this.infoDialog.yesCallback = null;
        this.infoDialog.visible = true;
      } finally {
        this.uploading = false;
        this.clockRecordsUploadFile = null;
      }
    },
    clearDate() {
      this.selectedDate = getTodayInTaipei();
    },
    changeDate(offset) {
      const current = new Date(this.selectedDate);
      current.setDate(current.getDate() + offset);
      this.selectedDate = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Taipei",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(current);
    },
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.originalItem = Object.assign({}, item);
      this.initializePickers();
      this.editDialog = true;
    },
    initializePickers() {
      if (!this.editedItem) return;
      this.clockInActualTime = this.formatTimeHHmm(
        this.editedItem.clock_in_actual,
      );
      this.clockOutActualTime = this.formatTimeHHmm(
        this.editedItem.clock_out_actual,
      );
      this.lunchBreakStartActualTime = this.formatTimeHHmm(
        this.editedItem.lunch_break_start_actual,
      );
      this.lunchBreakEndActualTime = this.formatTimeHHmm(
        this.editedItem.lunch_break_end_actual,
      );
    },
    combineDateTime(dateStr, timeStr) {
      if (!dateStr || !timeStr) return null;
      return new Date(`${dateStr}T${timeStr}:00+08:00`).toISOString();
    },
    cancelEdit() {
      this.editDialog = false;
      this.editedItem = null;
    },
    async saveItem() {
      if (!this.editedItem) return;
      this.loading = true;
      try {
        const dateStr = this.editedItem.clock_date || this.selectedDate;
        const payload = {
          clock_in_actual: this.clockInActualTime
            ? this.combineDateTime(dateStr, this.clockInActualTime)
            : null,
          clock_out_actual: this.clockOutActualTime
            ? this.combineDateTime(dateStr, this.clockOutActualTime)
            : null,
          lunch_break_start_actual: this.lunchBreakStartActualTime
            ? this.combineDateTime(dateStr, this.lunchBreakStartActualTime)
            : null,
          lunch_break_end_actual: this.lunchBreakEndActualTime
            ? this.combineDateTime(dateStr, this.lunchBreakEndActualTime)
            : null,
        };

        if (this.editedItem.late !== this.originalItem.late) {
          payload.late = this.editedItem.late;
        }
        if (this.editedItem.early !== this.originalItem.early) {
          payload.early = this.editedItem.early;
        }

        const response = await this.$axios.put(
          `/api/clock-records/${this.editedItem.id}`,
          payload,
        );

        const updatedData = response.data.data;
        const index = this.records.findIndex(
          (r) => r.id === this.editedItem.id,
        );
        if (index !== -1 && updatedData) {
          const existing = this.records[index];
          this.records.splice(index, 1, { ...existing, ...updatedData });
        }

        this.infoDialog = {
          visible: true,
          title: "成功",
          message: "更新打卡紀錄成功",
          yesButtonText: "確定",
          yesCallback: () => {
            this.infoDialog.visible = false;
          },
        };
        this.editDialog = false;
        this.editedItem = null;
        this.$emit("success", "更新成功");
      } catch (error) {
        this.infoDialog = {
          visible: true,
          title: "錯誤",
          message:
            error?.response?.data?.message ||
            error.message ||
            "更新打卡紀錄失敗",
          yesButtonText: "確定",
          yesCallback: () => {
            this.infoDialog.visible = false;
          },
        };
      } finally {
        this.loading = false;
      }
    },
    async viewHistory(item) {
      this.currentHistoryRecordId = item.id;
      this.historyPage = 1;
      this.historyDialog = true;
      await this.loadHistory();
    },
    async loadHistory() {
      if (!this.currentHistoryRecordId) return;

      this.historyLoading = true;
      this.historyError = null;

      try {
        const queryParams = new URLSearchParams({
          page: this.historyPage,
          limit: this.historyMeta.limit,
        });

        const response = await this.$axios.get(
          `/api/history/clock-records/${this.currentHistoryRecordId}?${queryParams}`,
        );

        this.historyRecords = response.data.data || [];
        this.historyMeta = response.data.meta || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        };
      } catch (error) {
        this.historyError =
          error?.response?.data?.message || error.message || "無法載入歷史記錄";
        this.historyRecords = [];
      } finally {
        this.historyLoading = false;
      }
    },
    closeHistory() {
      this.historyDialog = false;
      this.currentHistoryRecordId = null;
      this.historyRecords = [];
      this.historyError = null;
      this.historyPage = 1;
    },
    getFieldLabel(field) {
      const labels = {
        clock_in_actual: "實際上班",
        clock_out_actual: "實際下班",
        lunch_break: "午休",
        lunch_break_start_actual: "午休實際開始",
        lunch_break_end_actual: "午休實際結束",
        overtime: "加班",
        overtime_apply_id: "加班申請",
        overtime_record_id: "加班申請",
        late: "遲到",
        early: "早退",
        clock_in_time: "排定上班",
        clock_out_time: "排定下班",
        lunch_break_start: "午休排定開始",
        lunch_break_end: "午休排定結束",
      };
      return labels[field] || field;
    },
    formatFieldValue(field, value) {
      if (value === null || value === undefined) {
        return "-";
      }

      // Handle datetime fields
      const dateTimeFields = [
        "clock_in_actual",
        "clock_out_actual",
        "lunch_break_start_actual",
        "lunch_break_end_actual",
        "clock_in_time",
        "clock_out_time",
        "lunch_break_start",
        "lunch_break_end",
      ];

      if (dateTimeFields.includes(field)) {
        return this.formatDateTime(value);
      }

      // Handle boolean fields
      if (field === "lunch_break") {
        return value ? "是" : "否";
      }

      if (field === "overtime") {
        return value === true ? "是" : value === false ? "否" : "-";
      }

      // Handle numeric fields
      if (typeof value === "number") {
        return value.toString();
      }

      return value;
    },
    isValidGPS(gps) {
      if (!gps || typeof gps !== "string") return false;
      const parts = gps.split(",");
      if (parts.length !== 2) return false;
      const lat = parseFloat(parts[0].trim());
      const lon = parseFloat(parts[1].trim());
      return !isNaN(lat) && !isNaN(lon);
    },
    openGPSInGoogleMaps(gps) {
      if (!this.isValidGPS(gps)) return;
      const [latStr, lonStr] = gps.split(",");
      const lat = parseFloat(latStr.trim());
      const lon = parseFloat(lonStr.trim());
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
      window.open(url, "_blank");
    },
    formatTimeHHmm(value) {
      if (!value) return "";
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        if (typeof value === "string" && value.includes(":")) {
          const parts = value.split(":");
          return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
        }
        return value;
      }
      return date.toLocaleTimeString("zh-TW", {
        timeZone: "Asia/Taipei",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    getTimelineEvents(item) {
      if (!item) return [];
      if (Array.isArray(item.timeline_events) && item.timeline_events.length > 0) {
        return item.timeline_events;
      }
      const events = [];

      // Lateness indicator if late > 0 and scheduled clock-in exists
      if (item.late > 0 && item.clock_in_time) {
        events.push({
          id: `late_${item.id}`,
          type: "late",
          time: new Date(item.clock_in_time).getTime(),
          label: `遲到${this.formatTimeHHmm(item.clock_in_time)}`,
          isAlert: true,
        });
      }

      // Actual Clock In
      if (item.clock_in_actual) {
        events.push({
          id: `cin_${item.id}`,
          type: "clock_in",
          time: new Date(item.clock_in_actual).getTime(),
          label: `上班${this.formatTimeHHmm(item.clock_in_actual)}`,
          gps: item.gps_clock_in,
          gpsKey: `${item.id}_in`,
          gpsTitle: "GPS 位置 (上班)",
        });
      }

      // Lunch break actual start & end
      if (item.lunch_break_start_actual) {
        events.push({
          id: `lb_start_${item.id}`,
          type: "lunch_start",
          time: new Date(item.lunch_break_start_actual).getTime(),
          label: `休息A開始${this.formatTimeHHmm(
            item.lunch_break_start_actual,
          )}`,
          gps: item.gps_lunch_break_start,
          gpsKey: `${item.id}_lb_start`,
          gpsTitle: "GPS 位置 (午休開始)",
        });
      }
      if (item.lunch_break_end_actual) {
        events.push({
          id: `lb_end_${item.id}`,
          type: "lunch_end",
          time: new Date(item.lunch_break_end_actual).getTime(),
          label: `休息A結束${this.formatTimeHHmm(item.lunch_break_end_actual)}`,
          gps: item.gps_lunch_break_end,
          gpsKey: `${item.id}_lb_end`,
          gpsTitle: "GPS 位置 (午休結束)",
        });
      }

      // Rest 1 actual start & end
      if (item.rest_1_start_actual) {
        events.push({
          id: `rest1_start_${item.id}`,
          type: "rest_start",
          time: new Date(item.rest_1_start_actual).getTime(),
          label: `休息B開始${this.formatTimeHHmm(item.rest_1_start_actual)}`,
        });
      }
      if (item.rest_1_end_actual) {
        events.push({
          id: `rest1_end_${item.id}`,
          type: "rest_end",
          time: new Date(item.rest_1_end_actual).getTime(),
          label: `休息B結束${this.formatTimeHHmm(item.rest_1_end_actual)}`,
        });
      }

      // Leave records
      if (Array.isArray(item.leave_records)) {
        item.leave_records.forEach((leave) => {
          if (leave.start_time) {
            const leaveName = this.getLeaveTypeName(leave.leave_type);
            const startStr = this.formatTimeHHmm(leave.start_time);
            const endStr = leave.end_time
              ? this.formatTimeHHmm(leave.end_time)
              : "";
            const timeRange = endStr ? `${startStr}-${endStr}` : startStr;
            const hours = leave.leave_minutes
              ? parseFloat((leave.leave_minutes / 60).toFixed(1))
              : 0;

            const labelText =
              !item.clock_in_actual && !item.clock_out_actual && hours >= 8
                ? `${leaveName}${hours}小時`
                : `${leaveName}${timeRange}`;

            events.push({
              id: `leave_${item.id}_${leave.id}`,
              type: "leave",
              time: new Date(leave.start_time).getTime(),
              label: labelText,
              isAlert: true,
            });
          }
        });
      }

      // Overtime records
      if (Array.isArray(item.overtimeRecords)) {
        item.overtimeRecords.forEach((ot) => {
          if (ot.start_time) {
            const startStr = this.formatTimeHHmm(ot.start_time);
            const endStr = ot.end_time ? this.formatTimeHHmm(ot.end_time) : "";
            const timeRange = endStr ? `${startStr}-${endStr}` : startStr;
            events.push({
              id: `ot_${item.id}_${ot.id}`,
              type: "overtime",
              time: new Date(ot.start_time).getTime(),
              label: `加班${timeRange}`,
            });
          }
        });
      }

      // Actual Clock Out
      if (item.clock_out_actual) {
        events.push({
          id: `cout_${item.id}`,
          type: "clock_out",
          time: new Date(item.clock_out_actual).getTime(),
          label: `下班${this.formatTimeHHmm(item.clock_out_actual)}`,
          gps: item.gps_clock_out,
          gpsKey: `${item.id}_out`,
          gpsTitle: "GPS 位置 (下班)",
        });
      }

      // Missing punches / absent state fallback
      if (events.length === 0) {
        if (item.clock_in_time && item.clock_out_time) {
          const shiftStart = this.formatTimeHHmm(item.clock_in_time);
          const shiftEnd = this.formatTimeHHmm(item.clock_out_time);
          events.push({
            id: `absent_${item.id}`,
            type: "absent",
            time: new Date(item.clock_in_time).getTime(),
            label: `排班${shiftStart}-${shiftEnd} (未打卡)`,
            isAlert: true,
          });
        } else {
          events.push({
            id: `absent_${item.id}`,
            type: "absent",
            time: 0,
            label: "未打卡",
            isAlert: true,
          });
        }
      }

      // Type priorities when times match: actual punches rank before leaves and overtime
      const typePriority = {
        late: 1,
        clock_in: 2,
        lunch_start: 3,
        lunch_end: 4,
        rest_start: 5,
        rest_end: 6,
        clock_out: 7,
        leave: 8,
        overtime: 9,
        absent: 10,
      };

      // Sort by minute, then by type priority (punches before leave/overtime), then by timestamp
      events.sort((a, b) => {
        const minA = Math.floor((a.time || 0) / 60000);
        const minB = Math.floor((b.time || 0) / 60000);
        if (minA !== minB) {
          return minA - minB;
        }
        const prioA = typePriority[a.type] || 99;
        const prioB = typePriority[b.type] || 99;
        if (prioA !== prioB) {
          return prioA - prioB;
        }
        return (a.time || 0) - (b.time || 0);
      });
      return events;
    },
    getWorkHoursBreakdown(item) {
      if (!item) return { regular: "", ot1: "", ot2: "", workHours: "" };

      return {
        regular:
          item.regular_hours !== null &&
          item.regular_hours !== undefined &&
          item.regular_hours !== ""
            ? item.regular_hours
            : "",
        ot1:
          item.overtime_tier1 !== null &&
          item.overtime_tier1 !== undefined &&
          item.overtime_tier1 !== ""
            ? item.overtime_tier1
            : "",
        ot2:
          item.overtime_tier2 !== null &&
          item.overtime_tier2 !== undefined &&
          item.overtime_tier2 !== ""
            ? item.overtime_tier2
            : "",
        workHours:
          item.work_hours !== null &&
          item.work_hours !== undefined &&
          item.work_hours !== ""
            ? item.work_hours
            : "",
      };
    },
    getNotesInfo(item) {
      if (!item) return [];
      if (Array.isArray(item.notes_info) && item.notes_info.length > 0) {
        return item.notes_info;
      }
      if (Array.isArray(item.notes) && item.notes.length > 0) {
        return item.notes;
      }
      const notes = [];

      // 1. Leave summaries
      if (Array.isArray(item.leave_records) && item.leave_records.length > 0) {
        item.leave_records.forEach((l) => {
          const lName = this.getLeaveTypeName(l.leave_type);
          const hours = l.leave_minutes
            ? parseFloat((l.leave_minutes / 60).toFixed(1))
            : 0;
          notes.push({ text: `${lName}${hours}小時`, isAlert: true });
        });
      }

      // 2. Overtime status
      const hasOvertime =
        (Array.isArray(item.overtimeRecords) &&
          item.overtimeRecords.length > 0) ||
        item.overtime === true;
      if (!hasOvertime) {
        if (item.clock_in_actual || (item.status && item.status.includes(2))) {
          notes.push({ text: "無加班", color: "#548dd4" });
        }
      } else {
        if (item.is_holiday) {
          notes.push({ text: "國定假日加班", isAlert: true });
        } else if (item.is_recess) {
          notes.push({ text: "休息日加班", isAlert: true });
        }
      }

      // 3. Extra notes
      if (typeof item.extra === "object" && item.extra) {
        Object.entries(item.extra).forEach(([k, v]) => {
          if (k === "re_clock_in") {
            notes.push({ text: `請假期間上班: ${this.formatTimeHHmm(v)}` });
          } else if (k === "re_clock_out") {
            notes.push({ text: `請假期間下班: ${this.formatTimeHHmm(v)}` });
          } else {
            notes.push({ text: String(v) });
          }
        });
      } else if (item.extra) {
        notes.push({ text: String(item.extra) });
      }

      // 4. Absent note
      if (
        !item.clock_in_actual &&
        !item.clock_out_actual &&
        (!item.leave_records || item.leave_records.length === 0)
      ) {
        notes.push({ text: "缺勤", isAlert: true });
      }

      return notes;
    },
  },
};
</script>

<style scoped>
.table >>> thead th {
  white-space: nowrap;
  font-size: 1rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: #8392ab;
  opacity: 0.7;
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:last-child td {
  border-bottom: 0;
}

.table >>> tbody td {
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}

.table >>> tbody tr:hover {
  background: #f8f9fa;
}

.table >>> thead th.number-col-header {
  white-space: normal !important;
  line-height: 1.15 !important;
  font-size: 0.75rem !important;
  padding-left: 2px !important;
  padding-right: 2px !important;
  text-align: center !important;
}

.table >>> tbody td.number-col-cell {
  padding-left: 2px !important;
  padding-right: 2px !important;
  white-space: nowrap !important;
  text-align: center !important;
}

.header-multiline {
  line-height: 1.15;
  letter-spacing: 0;
  word-break: keep-all;
  font-size: 0.75rem;
}
</style>
